/**
 * Procreate's hand gestures, on the web.
 *
 * Two fingers undo, three redo, four take the interface away; a pinch zooms and a quick
 * one fits the drawing to the screen; three fingers swiped down ask for the menu. They
 * are worth the trouble because they are how a drawing app gets out of its own way: the
 * hand not holding the pen does the work a toolbar would otherwise ask for.
 *
 * Only touches are watched. A mouse and a trackpad keep the wheel and the keys, because
 * a trackpad reports no fingers — a two-finger tap on one is indistinguishable from a
 * click, and guessing would cost an undo every time someone rested a hand.
 */
export interface GestureHandlers {
  onUndo(): void;
  onRedo(): void;
  onFullScreen(): void;
  /** A quick pinch, which is Procreate's "fit the drawing to the screen". */
  onFit(): void;
  /** Three fingers down the canvas: the copy and paste menu, at this point. */
  onMenu(at: { x: number; y: number }): void;
  /**
   * One finger held still: the canvas menu a right-click opens, which a touch screen has no other
   * way to ask for (the responsive pass, 2026-10-26). The page decides whether the tool in hand lets it.
   */
  onHold?(at: { x: number; y: number }): void;
  /** A pinch: the scale since the last move, around a point in screen coordinates. */
  onZoom(factor: number, at: { x: number; y: number }): void;
  /** Two fingers moving together. */
  onPan(dx: number, dy: number): void;
  /** True while more than one finger is down, so tools stay out of the way. */
  onMultiTouch(active: boolean): void;
}

interface Touch {
  readonly id: number;
  readonly startX: number;
  readonly startY: number;
  x: number;
  y: number;
  moved: number;
}

/** A tap is short, and a finger that travels this far was doing something else. */
const TAP_MS = 300;
const TAP_SLOP = 12;
/** A pinch this quick is a request to fit rather than a zoom. */
const QUICK_PINCH_MS = 260;
const QUICK_PINCH_CHANGE = 0.25;
/** How far three fingers travel down before it counts as a swipe. */
const SWIPE = 70;
/** How long one finger rests before it is a long-press, and how far it may wander meanwhile. */
export const HOLD_MS = 500;
export const HOLD_SLOP = 8;

export function watchGestures(canvas: HTMLElement, handlers: GestureHandlers): () => void {
  const touches = new Map<number, Touch>();
  let most = 0;
  let startedAt = 0;
  let pinchStart: { distance: number; at: number } | null = null;
  /**
   * The spread as the first of two fingers lifted, while both were still down.
   *
   * Judged at the LAST lift instead, there was no other finger left to measure against,
   * so a still two-finger tap read as a pinch from full spread to nothing — and zoomed
   * to Fit instead of undoing (QA 2026-09-24, sel-F6).
   */
  let pinchEnd: number | null = null;
  /** The farthest any finger of this gesture travelled. */
  let travelled = 0;
  let previous: { distance: number; x: number; y: number } | null = null;
  let swiped = false;
  let hold: ReturnType<typeof setTimeout> | 0 = 0;
  const letGo = () => {
    clearTimeout(hold);
    hold = 0;
  };

  const centre = () => {
    const all = [...touches.values()];
    const x = all.reduce((sum, touch) => sum + touch.x, 0) / all.length;
    const y = all.reduce((sum, touch) => sum + touch.y, 0) / all.length;
    return { x, y };
  };
  const spread = () => {
    const all = [...touches.values()];
    if (all.length < 2) return 0;
    return Math.hypot(all[0]!.x - all[1]!.x, all[0]!.y - all[1]!.y);
  };

  const onDown = (event: PointerEvent) => {
    if (event.pointerType !== 'touch') return;
    if (touches.size === 0) {
      startedAt = performance.now();
      most = 0;
      swiped = false;
      pinchEnd = null;
      travelled = 0;
    }
    touches.set(event.pointerId, { id: event.pointerId, startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY, moved: 0 });
    most = Math.max(most, touches.size);
    letGo();
    if (touches.size === 1 && handlers.onHold) {
      const at = { x: event.clientX, y: event.clientY };
      hold = setTimeout(() => {
        hold = 0;
        // Still one finger, still where it went down: nothing else has claimed this press.
        if (touches.size === 1 && travelled === 0 && [...touches.values()].every((one) => one.moved <= HOLD_SLOP)) handlers.onHold?.(at);
      }, HOLD_MS);
    }
    if (touches.size === 2) {
      pinchStart = { distance: spread(), at: performance.now() };
      previous = { distance: spread(), ...centre() };
    }
    if (touches.size > 1) handlers.onMultiTouch(true);
  };

  const onMove = (event: PointerEvent) => {
    const touch = touches.get(event.pointerId);
    if (!touch) return;
    touch.moved += Math.hypot(event.clientX - touch.x, event.clientY - touch.y);
    touch.x = event.clientX;
    touch.y = event.clientY;
    if (hold && Math.hypot(touch.x - touch.startX, touch.y - touch.startY) > HOLD_SLOP) letGo();

    if (touches.size === 2 && previous) {
      const distance = spread();
      const at = centre();
      if (previous.distance > 0 && distance > 0) handlers.onZoom(distance / previous.distance, at);
      handlers.onPan(at.x - previous.x, at.y - previous.y);
      previous = { distance, x: at.x, y: at.y };
      return;
    }

    if (touches.size === 3 && !swiped) {
      // Three fingers down the canvas: the copy and paste menu.
      const all = [...touches.values()];
      const down = all.every((item) => item.y - item.startY > SWIPE);
      if (down) {
        swiped = true;
        handlers.onMenu(centre());
      }
    }
  };

  const onUp = (event: PointerEvent) => {
    const touch = touches.get(event.pointerId);
    if (!touch) return;
    travelled = Math.max(travelled, touch.moved);
    letGo();
    if (touches.size === 2) pinchEnd = spreadBetween(touch);
    touches.delete(event.pointerId);

    if (touches.size === 0) {
      handlers.onMultiTouch(false);
      const elapsed = performance.now() - startedAt;
      const quiet = travelled <= TAP_SLOP;

      // A pinch that came and went in a moment is a request to fit, not a zoom.
      if (most === 2 && pinchStart && elapsed < QUICK_PINCH_MS) {
        const change = Math.abs(pinchStart.distance - (pinchEnd ?? pinchStart.distance)) / Math.max(1, pinchStart.distance);
        if (change > QUICK_PINCH_CHANGE || !quiet) {
          handlers.onFit();
          pinchStart = null;
          previous = null;
          return;
        }
      }

      if (!swiped && quiet && elapsed < TAP_MS) {
        // Tap with two fingers to undo, three to redo, four for full screen: the
        // handbook's own counts, and the reason a drawing app needs no undo button.
        if (most === 2) handlers.onUndo();
        else if (most === 3) handlers.onRedo();
        else if (most >= 4) handlers.onFullScreen();
      }
      pinchStart = null;
      previous = null;
    } else if (touches.size === 1) {
      previous = null;
    }
  };

  /** The spread between a lifting finger and the one still down, for judging a quick pinch. */
  const spreadBetween = (lifted: Touch) => {
    const other = [...touches.values()].find((one) => one.id !== lifted.id);
    return other ? Math.hypot(lifted.x - other.x, lifted.y - other.y) : 0;
  };

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onUp);
  return () => {
    letGo();
    canvas.removeEventListener('pointerdown', onDown);
    canvas.removeEventListener('pointermove', onMove);
    canvas.removeEventListener('pointerup', onUp);
    canvas.removeEventListener('pointercancel', onUp);
  };
}
